import { useNavigate } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, X, ArrowRight, Cpu, MousePointerClick, MonitorSmartphone, Tag, FileDown, Share2, Laptop, ShieldCheck, Zap } from "lucide-react";

const GAP_LOSE = [
  { t: "Passive scroll, not presence", d: "A learner is watching a 2D feed. Attention drifts the moment the page is split between tabs, chat, and the lesson window." },
  { t: "No spatial context", d: "A diagram of a cell wall or a Civil War battlefield is a thumbnail. The learner never occupies the space the standard is testing." },
  { t: "No embodiment, no recall", d: "Quizzes at the end of a module test memory of labels, not the ability to mentally place themselves in the subject." },
];
const GAP_WIN = [
  { t: "Presence — a learner is inside it", d: "A 3D scene pulls the same focus a window never does. Students who drift on a feed stay with a place they can move around in." },
  { t: "Exploration, not narration", d: "Learners walk into the scene, point at objects, and follow their own curiosity. The lesson responds to what they choose to look at." },
  { t: "Embodied recall", d: "Spatial memory carries what rote recall drops. Students remember what they stood inside, not what they read twice." },
];
const CAPS = [
  { icon: Cpu, tag: "Core", t: "AI prompt-to-scene generator", d: "Describe the lesson objective in plain language — the AI builds a 3D scene, interactions, and a narrative flow that fits the standard." },
  { icon: MousePointerClick, tag: "Educator-first", t: "No coding or 3D skill needed", d: "Educators without a technical background generate, customize, and publish lessons through a visual lesson builder — no production pipeline." },
  { icon: MonitorSmartphone, tag: "Coverage", t: "Device-agnostic playback", d: "Full VR and WebGL on desktop or headset. A lightweight, interactive 2D fallback on the Chromebook or tablet a cyber student already uses." },
  { icon: Tag, tag: "Compliance", t: "CCSS + NGSS standards tagging", d: "Every generated lesson maps to the standards you specify. Review the codes before publishing, so coverage matches PA reporting out of the box." },
  { icon: FileDown, tag: "Export", t: "Printable lesson plan export", d: "Export a PDF lesson plan with objectives, vocabulary, scene walkthrough, and assessment prompts — drop-in for existing unit planning." },
  { icon: Share2, tag: "Distribution", t: "Per-class student link sharing", d: "One click generates a class cohort link students open on their own device — no login wall for the learner, no per-student account to provision." },
];
const WHY = [
  { icon: Laptop, t: "No headset required", d: "A cyber student runs the lesson on the Chromebook they already open for class — full WebGL in the browser, no peripheral purchase, no hardware rollout." },
  { icon: ShieldCheck, t: "Standards-aligned from day one", d: "CCSS and NGSS tagging ships with every lesson, mapped to the codes PA reporting expects. Teachers approve codes before publishing — auditable without a separate pass." },
  { icon: Zap, t: "Fast for teachers", d: "A standards-tagged scene ships in under a minute from prompt to publishable draft. No production pipeline means a teacher can iterate between lessons." },
];
const MARKET = [
  { n: "$31B → $81B", l: "VR-in-education market, 2025 → 2030 (industry estimate)" },
  { n: "Cyber / online", l: "Natural early adopters — all-remote learners, no on-site tech handoff" },
  { n: "Standards-aligned", l: "The unlock schools cite most for adoption" },
];
const TERMS = [
  ["Starting subjects", "Earth Science, U.S. History, Biology, Literature, Mathematics."],
  ["Seats", "30 seats per subject, five starting subjects enrolled (~150 seats across the pilot). Each subject is its own cohort."],
  ["Pricing", "Priced per subject at $199/month, locked-in for the life of the pilot. Each enrolled subject is billed independently — not bundled."],
  ["Term", "One semester (~5 months), same term across all five subjects. Month-to-month under the rate lock."],
  ["Standards", "CCSS + NGSS tagging on every generated lesson."],
  ["Equipment", "No VR headset required — fully browser-based."],
  ["Checkpoint", "Structured feedback review at week 4–6."],
  ["Cancellation", "30-day notice; no early-term fees."],
  ["Next step", "Path to a school-wide district license at per-subject negotiated pricing, contingent on a successful pilot."],
];
const FAQ = [
  ["Is a headset required for students?", "No. The pilot is fully browser-based — students run lessons in full WebGL on the school-issued Chromebook they already use. A VR headset is optional for full immersive mode, never required."],
  ["What does teacher onboarding look like?", "A short founder-led session. Teachers generate their first standards-tagged lesson in the first sitting — there is no production pipeline or new authoring tool to learn."],
  ["How are lessons standards-tagged?", "Every generated lesson maps to the CCSS + NGSS codes you specify. Teachers review and approve the codes before publishing, so coverage matches PA reporting out of the box."],
  ["What happens at the week 4–6 checkpoint?", "A structured feedback review of usage and early outcomes, subject by subject, to decide what to continue, adjust, or expand for the rest of the semester."],
  ["How does the path to a school-wide license work?", "A successful pilot leads to a school-wide license at per-subject negotiated pricing — you scale the subjects that proved out, on the same transparent per-subject basis."],
  ["What happens at the end of the semester?", "You're month-to-month under the rate lock. Continue, expand to more subjects, or stop with 30-day notice — no early-term fees either way."],
  ["Which subjects are included and how are seats counted?", "Earth Science, U.S. History, Biology, Literature, and Mathematics to start — ~30 seats each (~150 across the pilot). Each subject is its own cohort, evaluated and priced independently."],
];

export default function PaCyberPilot() {
  const nav = useNavigate();
  const mailto = "mailto:hello@vividvrlessons.com?subject=PA%20Cyber%20Pilot%20Proposal%20%E2%80%94%20Schedule%2020-min%20call";
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      {/* Hero */}
      <section className="hero-noise">
        <div className="max-w-5xl mx-auto px-6 py-16 animate-in-up">
          <Badge className="bg-primary/10 text-primary border-0 mb-4">Pilot Partnership Proposal · For PA Cyber Charter School leadership</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.05]">
            Bringing immersive VR lessons to PA Cyber learners.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Screen-only remote learning gives students a window — it doesn't put them inside the subject. VividVR turns a
            lesson prompt into a 3D scene a learner can step into in roughly 20 seconds, on the Chromebook they already
            open every morning. The pilot starts in five subjects — Earth Science, U.S. History, Biology, Literature, and Mathematics —
            with ~30 seats each (~150 across the five). Each subject is evaluated and priced on its own. No headset
            required, no production pipeline, no new platform for teachers to learn.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" data-testid="pacyber-call-btn" asChild className="rounded-full h-12 px-7">
              <a href={mailto}>Request a 20-minute call <ArrowRight className="w-4 h-4 ml-1" /></a>
            </Button>
            <Button size="lg" variant="outline" data-testid="pacyber-generate-btn" onClick={() => nav("/register")} className="rounded-full h-12 px-7">
              Generate a sample lesson yourself
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16 space-y-20">
        {/* Engagement gap */}
        <section>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2">The engagement gap</div>
          <h2 className="text-3xl font-display font-bold tracking-tight">Why screen-only remote learning loses attention</h2>
          <p className="text-muted-foreground mt-3 max-w-3xl">
            Cyber and online learners feel the friction of screen-only modules differently — there is no classroom to catch
            them when their attention leaves the page. Immersive VR fills the gap the feed leaves open.
          </p>
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="rounded-3xl border border-border bg-card p-7">
              <div className="font-display font-semibold text-lg mb-4">What screen-only learning leaves on the table</div>
              <div className="space-y-4">
                {GAP_LOSE.map((g) => (
                  <div key={g.t} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-red-500/10 text-red-600 flex items-center justify-center shrink-0"><X className="w-4 h-4" /></div>
                    <div><div className="font-semibold text-sm">{g.t}</div><div className="text-sm text-muted-foreground">{g.d}</div></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border-2 border-primary/30 bg-primary/5 p-7">
              <div className="font-display font-semibold text-lg mb-4">What immersive VR unlocks for cyber learners</div>
              <div className="space-y-4">
                {GAP_WIN.map((g) => (
                  <div key={g.t} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0"><Check className="w-4 h-4" /></div>
                    <div><div className="font-semibold text-sm">{g.t}</div><div className="text-sm text-muted-foreground">{g.d}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2">What VividVR does</div>
          <h2 className="text-3xl font-display font-bold tracking-tight">Six capabilities a cyber teaching team uses on day one</h2>
          <p className="text-muted-foreground mt-3 max-w-3xl">
            The full platform — generation through standards tagging through student rollout — is shipped under one login.
            No bolt-ons, no production team, no headset purchase.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {CAPS.map((c) => (
              <div key={c.t} className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><c.icon className="w-5 h-5" /></div>
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">{c.tag}</Badge>
                </div>
                <h3 className="font-display font-semibold mt-4">{c.t}</h3>
                <p className="text-sm text-muted-foreground mt-2">{c.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why PA Cyber */}
        <section>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2">Why PA Cyber specifically</div>
          <h2 className="text-3xl font-display font-bold tracking-tight">Built for a cyber charter's operating model</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            {WHY.map((w) => (
              <div key={w.t} className="rounded-2xl border border-border bg-card p-6">
                <div className="w-11 h-11 rounded-xl bg-accent/10 text-accent flex items-center justify-center"><w.icon className="w-5 h-5" /></div>
                <h3 className="font-display font-semibold mt-4">{w.t}</h3>
                <p className="text-sm text-muted-foreground mt-2">{w.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Market context */}
        <section className="grid md:grid-cols-3 gap-4">
          {MARKET.map((m) => (
            <div key={m.l} className="rounded-2xl bg-secondary p-6">
              <div className="text-2xl font-display font-extrabold text-primary">{m.n}</div>
              <div className="text-sm text-muted-foreground mt-1">{m.l}</div>
            </div>
          ))}
        </section>

        {/* Pricing & terms */}
        <section>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2">Pilot proposal</div>
          <h2 className="text-3xl font-display font-bold tracking-tight">Pricing &amp; terms at a glance</h2>
          <p className="text-muted-foreground mt-3 max-w-3xl">
            Five starting subjects, 30 seats each, one locked-in per-subject rate. The offer is evaluated per subject — not
            bundled into a single pilot-wide rate. No surprise add-ons on either side of the table.
          </p>
          <div className="mt-8 rounded-3xl border border-border overflow-hidden">
            {TERMS.map(([k, v], i) => (
              <div key={k} className={`grid sm:grid-cols-3 gap-2 px-5 py-4 ${i % 2 ? "bg-card" : "bg-secondary/40"}`}>
                <div className="font-semibold text-sm">{k}</div>
                <div className="sm:col-span-2 text-sm text-muted-foreground">{v}</div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2">Pilot FAQ</div>
          <h2 className="text-3xl font-display font-bold tracking-tight mb-6">Questions a district decision-maker asks first</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={i} value={`f${i}`} className="rounded-xl border border-border bg-card px-4">
                <AccordionTrigger className="hover:no-underline text-left font-semibold">{q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* CTA */}
        <section className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-10 sm:p-14 text-center">
          <Badge className="bg-white/10 text-white border-0 mb-3">Earth Science · U.S. History · Biology · Literature · Mathematics</Badge>
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">Three ways to start the pilot conversation</h2>
          <p className="mt-3 text-white/80 max-w-2xl mx-auto">
            A founder-led 20-minute call is the fastest path. Prefer email? Send a few context lines. Or see what your team
            can build first — the open generator is one click away.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" asChild className="rounded-full h-12 px-7 bg-white text-slate-900 hover:bg-slate-100">
              <a href={mailto}>Request a 20-minute call <ArrowRight className="w-4 h-4 ml-1" /></a>
            </Button>
            <Button size="lg" variant="outline" onClick={() => nav("/register")} className="rounded-full h-12 px-7 bg-transparent border-white/30 text-white hover:bg-white/10">
              Generate a sample lesson
            </Button>
          </div>
          <p className="text-sm text-white/60 mt-5">Or email <a href="mailto:hello@vividvrlessons.com" className="text-white font-semibold">hello@vividvrlessons.com</a> — the founder reads every message.</p>
        </section>
      </div>
    </div>
  );
}
